import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import {
  useIsNewSessionListEnabled,
  useIsSessionRoutineRunsEnabled,
} from '@/features/flags';
import { releaseRecordingStateAfterSweep } from '@/hooks/desktop/releaseRecordingStateAfterSweep';
import { invalidateTeamMemberSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import { sessionCreated } from '@/lib/analytics/scribe/session';
import {
  cancelSessionCreateAttempt,
  failOwnedSessionCreateAttempt,
  type SessionCreateAttemptOwner,
} from '@/lib/telemetry/sessionCreateTracking';
import { SessionCreatedEntryPoint } from '@/models/analytics/features/sessions';
import { trackSessionCreated } from '@/models/analytics/features/trackSessionLifecycleAnalytics';
import { postCreateSessionForOtherUser } from '@/pages/api/scribeAPIV2';
import { sweepCaptureOnSessionEnd } from '@/tauri/online-chunk-upload/session-end-sweep';
import { logger } from '@/utils/logger/logger';
import { WORKFLOW_KEYS } from '@heidi/agents/contracts';
import { onSessionCreatedExpectRoutineRuns } from '@heidi/agents/data';
import { toast } from '@heidi/design-system/toast';
import { emitDomainEvent } from '@heidi/runtime/events';
import { sanitizeError } from '@heidi/runtime/logger';
import { appendCheckpoint, TrackingKey } from '@heidi/runtime/performance';
import { BaseStatus, SessionGroupStatus, SessionStatus } from '@heidi/schema/ml-scribe';
import {
  buildSessionPath,
  CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY,
  isLegacySessionDetailRoute,
  sessionCreatedEvent,
  type SessionsResponse,
  type SimpleSessionResponse,
} from '@heidi/session/contracts';
import { pendingStartNowSessionIdAtom } from '@heidi/session/data';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useStore } from 'jotai';
import { useRouter } from 'next/router';
import { GET_TEAM_MEMBER_SESSIONS } from '../query/useQuerySessions';
import {
  GET_INFINITE_TEAM_MEMBER_SESSIONS,
  useGetPageSize,
} from '../query/useQuerySessionsV2';
import { STOP_RECORDING_MUTATION_KEY } from './recordingMutationKeys';

export function useMutateCreateNewSessionForOtherUser(
  {
    navigate,
    autoStartTranscription = false,
  }: { navigate: boolean; autoStartTranscription?: boolean } = { navigate: true }
) {
  const queryClient = useQueryClient();
  const jotaiStore = useStore();
  const router = useRouter();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const pageSize = useGetPageSize();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isSessionRoutineRunsEnabled = useIsSessionRoutineRunsEnabled();

  return useMutation({
    mutationKey: [CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY],
    mutationFn: async ({
      teamId,
      memberId,
      attempt,
    }: {
      teamId: string;
      memberId: string;
      /** Set only by the caller that opened the `session_create` attempt. */
      attempt?: SessionCreateAttemptOwner;
    }) => {
      // 60s deadline — matches the create-session sibling; see RUM P99 in the
      // sibling comment.
      appendCheckpoint({
        key: TrackingKey.SCRIBE_NEW_SESSION,
        checkpointName: 'request_sent',
      });
      let response: Awaited<ReturnType<typeof postCreateSessionForOtherUser>>;
      try {
        response = await postCreateSessionForOtherUser({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          teamId,
          memberId,
          signal: AbortSignal.timeout(60_000),
        });
        appendCheckpoint({
          key: TrackingKey.SCRIBE_NEW_SESSION,
          checkpointName: 'response_received',
        });
        // The HTTP layer resolves empty when a 401 or a suspended account
        // takes the user out of the app, so no session was created.
        if (!response && attempt) cancelSessionCreateAttempt('interrupted');
      } catch (error) {
        failOwnedSessionCreateAttempt(attempt, error);
        throw error;
      }

      // FR-038: the second of `useNewSession`'s three creators. It owes the same
      // teardown as the own-user one WHEN IT NAVIGATES — creating FOR a
      // colleague does not make the recording still running on THIS desktop
      // somebody else's problem.
      //
      // AFTER the create, for the reason spelled out in the own-user sibling:
      // capture may only be stopped once the session it belongs to has ACTUALLY
      // ENDED. A create that fails leaves the clinician on the page they were
      // already on, still consulting, and a sweep at the top of `mutationFn`
      // would have silently killed that capture. This creator has no offline
      // fallback at all (see `onError` below), so a failed create here ends with
      // a toast and the clinician exactly where they were.
      //
      // Gated on `navigate`, which is this hook's own redirect switch (declared
      // on the signature above, consumed in `onSuccess` below): under
      // `navigate: false` THIS hook does not push. That is not the same as the
      // clinician staying put — a `navigate: false` caller commonly pushes to
      // the new session itself on the next line, which is what six of the seven
      // live `useMutateCreateNewSession` consumers do. Those callers keep
      // FR-038's shape and this gate excludes them; they are a pre-existing gap
      // this change does not close, listed with their pushing lines under
      // "Not closed by FR-038" in
      // `specs/012-app-8723-self-healing-stop/spec.md`.
      //
      // `anyActiveUpload`, matching the own-user create: the user's involvement
      // with whatever is still recording ends when they leave for the new
      // session, whichever session native names it under. Awaited so capture is
      // terminated before `onSuccess` pushes the redirect, and safe to ignore:
      // the helper never throws and never blocks the create on failure.
      if (navigate) {
        // Stand aside while the user's own stop drains: it holds
        // `RECORDING_STOP_COORDINATOR`, so this sweep's stop could only wait out
        // the deadline. Constraint in full: `useMutateCreateNewSession.ts`.
        const sweepResult = await sweepCaptureOnSessionEnd({
          target: { kind: 'anyActiveUpload' },
          callSite: 'createNewSessionForOtherUser',
          stopInFlight:
            queryClient.isMutating({ mutationKey: [STOP_RECORDING_MUTATION_KEY] }) > 0,
        });

        // Stopping native capture is only half the job, and the half that was
        // missing is the one the NEXT recording depends on. Whatever the sweep
        // terminated, the frontend still claimed a recording was in progress —
        // the shared timer, the live-session atom, the transport stamp, the
        // cached `recording_status`. A stale `timerModel.isRunning` does not
        // merely mislead the UI: `runRecorderSafetyStop` returns early on it
        // TWICE, so it also disables the last-resort force-stop for every
        // genuine runaway afterwards. Release the claim, and only on a result
        // that entitles it — the helper decides which do.
        //
        // `sessionId: null` because this call site targets `anyActiveUpload`: it
        // has no session of its own to release, so whichever session the
        // frontend still claims is the one released.
        releaseRecordingStateAfterSweep({
          result: sweepResult,
          sessionId: null,
          jotaiStore,
          queryClient,
        });
      }

      return response;
    },

    onSuccess: ({ data }, variables) => {
      appendCheckpoint({
        key: TrackingKey.SCRIBE_NEW_SESSION,
        checkpointName: 'session_created',
      });
      const sessionId = data.session_id;
      // Domain event (arch §5.2 Aperture 2) — creating ON BEHALF OF a
      // teammate still announces: session owns the mutation, so owner-only
      // emission holds.
      emitDomainEvent(sessionCreatedEvent, { sessionId });
      onSessionCreatedExpectRoutineRuns(
        sessionId,
        (id) => {
          void queryClient.invalidateQueries({
            queryKey: WORKFLOW_KEYS.sessionRuns(id),
          });
        },
        isSessionRoutineRunsEnabled
      );

      if (navigate) {
        // A user already sitting on the legacy page stays on it, so the flag
        // alone does not decide the route here.
        const pathname = buildSessionPath(
          sessionId,
          isNewSessionListEnabled && !isLegacySessionDetailRoute(router.pathname)
        );
        // Park before push (same order as self-create Start Now / Work Home).
        if (autoStartTranscription) {
          jotaiStore.set(pendingStartNowSessionIdAtom, {
            sessionId,
            parkedAt: Date.now(),
          });
        }
        // Fire-and-forget: navigation runs after `onSuccess` returns. Awaiting
        // `router.push` here would pin the mutation in `pending` until the new
        // route's data dependencies resolved.
        void router.push({ pathname });
      }

      // Optimistically update to the new value
      const placeHolderSession: SimpleSessionResponse = {
        session_id: sessionId,
        kinde_gp_id: data.kinde_gp_id,
        patient: {
          name: undefined,
          gender: undefined,
          dob: undefined,
          demographic_details: undefined,
        },
        status: BaseStatus.CREATED,
        is_consent: null,
        session_status: SessionStatus.EMPTY,
        is_urgent: false,
        session_group_status: SessionGroupStatus.no_action,
        duration: 0,
      };
      if (sessionQueryVersion === 'v2' || sessionQueryVersion === 'v3') {
        queryClient.setQueryData<InfiniteData<SessionsResponse>>(
          [
            GET_INFINITE_TEAM_MEMBER_SESSIONS,
            { teamId: variables.teamId },
            { memberId: variables.memberId },
            pageSize,
          ],
          (oldData) => {
            if (!oldData || oldData.pages.length === 0) {
              return oldData;
            }
            const newData = {
              ...oldData,
              pages: oldData.pages.map((page, index) => {
                if (index === 0) {
                  return {
                    ...page,
                    sessions: [placeHolderSession, ...page.sessions],
                    count: page.count + 1,
                  };
                }
                return page;
              }),
            };

            return newData;
          }
        );
      } else {
        queryClient.setQueryData<SessionsResponse>(
          [
            GET_TEAM_MEMBER_SESSIONS,
            { teamId: variables.teamId },
            { memberId: variables.memberId },
          ],
          (oldSessions) => {
            if (!oldSessions) {
              logger.debug('teamMemberSessions old is undefined');
              return oldSessions;
            }
            return {
              sessions: [placeHolderSession, ...oldSessions.sessions],
              count: oldSessions.sessions.length + 1,
              has_next_page: oldSessions.has_next_page,
            };
          }
        );
      }
      appendCheckpoint({
        key: TrackingKey.SCRIBE_NEW_SESSION,
        checkpointName: 'store_updated',
      });

      sessionCreated(sessionId);
      trackSessionCreated({
        session_id: sessionId,
        entry_point: SessionCreatedEntryPoint.Unknown,
        is_for_other_user: true,
      });
    },

    onSettled: (_, __, variables) => {
      void invalidateTeamMemberSessionsQuery(variables?.teamId, variables?.memberId);
    },

    onError: (error) => {
      logger.error('[useMutateCreateNewSessionForOtherUser] onError', {
        errorName: error.name,
        error: sanitizeError(error),
      });
      // Deliberately NOT suppressed while offline, unlike the own-user create.
      // That suppression is only defensible because `useNewSession` catches the
      // same rejection and completes the create through offline capture, so the
      // toast would caption a create that succeeded. Creating for ANOTHER
      // clinician has no such fallback — offline capture is single-user, and
      // neither call site passes an `onError` that routes anywhere — so a
      // suppressed toast here is a click that does nothing and says nothing.
      toast({
        tone: 'destructive',
        title: error.message,
      });
    },
  });
}

// Cache identities are single-site in `@heidi/session/contracts` (ARCFE-1001).
// These were DOUBLE-DECLARED here — the literals agreed, but two independent
// declarations of one cache key is an unguarded drift risk: a change to either
// side splits writes and reads across two caches. Imported (not just
// re-exported) because this module uses them locally too.
export { CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY };
